import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  DestroyRef,
  NgZone,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterModule, NavigationStart } from '@angular/router';
import { AuthService } from '@app/core/auth/auth';
import { SessionStorageService } from '@app/core/auth/session-storage';
import { CurrentApprovalText } from '@app/features/hospital/reviews/approval-definitions';
import { ReviewService } from '@app/features/hospital/reviews/reviews';
import { Review } from '@app/shared/models/review.model';
import { BreadcrumbComponent } from '@app/shared/components/breadcrumb/breadcrumb';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton';
import { TableComponent } from '@app/shared/components/table/table';
import { FetchState, PatchState } from '@app/shared/models/fetch-state.model';
import { TableColumn } from '@app/shared/models/table-column.model';
import { mdiTable } from '@mdi/js';
import { Observable, of, take, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog';
import { HttpClient } from '@angular/common/http';
import {
  CustomDateAdapter,
  dateFormatter,
} from '@app/shared/utils/date-formats/date-formats';
import { formatId } from '@app/shared/utils/id-formats/id-formats';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner';
import { YyyyMMddFormatPipe } from '@app/shared/pipes/yyyyMMddFormat/yyyyMMddFormat';
import { idFormatPipe } from '@app/shared/pipes/idFormat/idFormat';

@Component({
  selector: 'app-hospital-reviews',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatRadioModule,
    MatSortModule,
    MatTooltipModule,
    BreadcrumbComponent,
    TableComponent,
    SpinnerComponent,
    SkeletonComponent,
    YyyyMMddFormatPipe,
    idFormatPipe,
  ],
  templateUrl: './reviews.html',
  providers: [DatePipe, { provide: DateAdapter, useClass: CustomDateAdapter }],
  styleUrl: './reviews.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewsComponent implements OnInit {
  readonly mdiTable = mdiTable;

  reviewState$: Observable<FetchState<Review[]>> = of({ status: 'loading' });
  patchState$: Observable<PatchState> = of({ status: 'idle' });

  dataSource = new MatTableDataSource<Review>([]);
  columns: TableColumn<Review>[] = [];
  displayedColumns: string[] = [];

  selectedItem: Review | null = null;
  editForm!: FormGroup;
  isReadOnly = true;
  submitted = false;

  approvalDefinitions = '';
  errorMessage = '*必須項目を入力してください';
  errorMessageDefault = '*必須項目を入力してください';
  targetHospitalCode: string | null = null;
  targetHospitalName: string | null = null;
  readonly today = new Date();
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('table', { read: TableComponent }) tableCmp?: TableComponent;

  constructor(
    private readonly reviewService: ReviewService,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly sessionService: SessionStorageService,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.buildColumns();
    this.displayedColumns = this.columns.map((c) => c.key);

    //ページ遷移する際にフォームに入力されているデータを保存するための処理
    //あまりいい処理とは言えないが管理者画面に戻る際に必要で追加
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.persistCurrentDraft());

    if (this.authService.isAdminUser()) {
      this.targetHospitalCode = this.sessionService.getTargetHospitalCode();
      this.targetHospitalName = this.sessionService.getTargetHospitalName();
      if (!(this.targetHospitalCode && this.targetHospitalName)) {
        this.router.navigate(['admin', 'reviews']);
      }
    }
    const fromAdmin = this.sessionService.getFromAdmin() === '1';
    if (this.reviewService.isInitialLoad() || fromAdmin) {
      this.reviewService.fetchReviews(this.targetHospitalCode);
      this.sessionService.removeSelectedReviewId();
      if (fromAdmin) {
        const adminSelectedId = this.sessionService.getAdminSelectedReviewId();
        if (adminSelectedId) {
          this.sessionService.saveSelectedReviewId(adminSelectedId);
        }
        this.sessionService.removeFromAdmin();
      }
    }
    this.bindReviewState();

    this.patchState$ = this.reviewService.patchState$;
    this.patchState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => {
        if (s.status === 'saving') {
          this.editForm.disable({ emitEvent: false });
        } else if (s.status === 'idle') {
          if (!this.isReadOnly) this.editForm.enable({ emitEvent: false });
        }
      });

    this.http
      .get<{ current: CurrentApprovalText }>('assets/approval_texts.json')
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.approvalDefinitions = data.current.text;
      });
  }

  onSelect(row: Review): void {
    this.persistCurrentDraft();
    this.submitted = false;
    this.selectedItem = row;
    this.sessionService.saveSelectedReviewId(row.id);
    this.isReadOnly = !(
      row.reviewResult === null && row.status === 'processing'
    );
    this.buildForm(row);
  }

  saveEdit(): void {
    this.submitted = true;
    if (!this.selectedItem || this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        iconName: 'alert',
        message: [
          '登録後は内容の変更ができません。',
          '登録を実行してもよろしいでしょうか？',
        ],
        primaryText: '登録',
        secondaryText: 'キャンセル',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === true && this.selectedItem) {
          const reviewed = this.datePipe.transform(
            this.editForm.value.reviewed,
            'yyyy-MM-dd',
          );

          const comment = this.editForm.value.comment ?? '';
          const sanitizedComment =
            comment
              .replace(/<[^>]*>/g, '')
              .slice(0, 300)
              .trim() || null;

          const patch: Partial<Review> = {
            resourceType: 'Review',
            id: this.selectedItem.id,
            reviewed,
            reviewResult: this.editForm.value.reviewResult,
            approver: {
              id: this.sessionService.getUserId(),
              name: this.sessionService.getUserName(),
            },
            comment: sanitizedComment,
            updater: {
              id: this.sessionService.getUserId(),
              name: this.sessionService.getUserName(),
            },
          };

          this.registerReviewResult(patch);
        }
      });
  }

  goToDetail(id: string): void {
    this.router.navigate(['hospital', 'reviews', formatId(id), 'data-types']);
  }

  private bindReviewState(): void {
    this.reviewState$ = this.reviewService.reviewState$;
    this.reviewState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.status !== 'success') return;

        const rows = state.data.filter((r) => r.status !== 'preparation');
        const rowsSorted = [...rows].sort((a, b) =>
          b.created.localeCompare(a.created),
        );
        this.dataSource.data = rowsSorted;
        const storedId = this.sessionService.getSelectedReviewId();
        const target =
          (storedId && rowsSorted.find((r) => r.id === storedId)) ||
          (!this.selectedItem && rowsSorted[0]) ||
          null;
        if (target) {
          this.onSelect(target);
          this.ngZone.onStable
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.tableCmp?.scrollToRowById(target.id);
            });
        }
      });
  }

  private buildForm(row?: Review): void {
    this.isReadOnly = row
      ? !(row.reviewResult === null && row.status === 'processing')
      : true;

    const draft = row ? this.reviewService.getDraft(row.id) : undefined;
    this.editForm = this.fb.group({
      reviewed: [
        {
          value: row?.reviewed ?? draft?.reviewed ?? '',
          disabled: this.isReadOnly,
        },
        [Validators.required, this.futureDateValidator()],
      ],
      comment: [
        {
          value: row?.comment ?? draft?.comment ?? '',
          disabled: this.isReadOnly,
        },
        [Validators.maxLength(300)],
      ],
      reviewResult: [
        {
          value: row?.reviewResult ?? draft?.reviewResult ?? '',
          disabled: this.isReadOnly,
        },
        [Validators.required, this.reviewResultEnumValidator()],
      ],
    });

    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
    this.editForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  private registerReviewResult(patch: Partial<Review>): void {
    this.editForm.disable({ emitEvent: false });

    this.reviewService.patchReview(patch.id as string, patch);
    this.patchState$
      .pipe(
        filter((s) => s.status === 'success' || s.status === 'error'),
        take(1),
      )
      .subscribe((s) => {
        if (s.status === 'success') {
          this.reviewService.fetchReviews(this.targetHospitalCode);
        } else {
          const ref = this.snackBar.open(
            '登録に失敗しました。\nしばらくしてから再度お試しください。',
            '×',
            {
              panelClass: 'error-toast',
              verticalPosition: 'top',
              horizontalPosition: 'center',
            },
          );
          ref
            .onAction()
            .pipe(take(1))
            .subscribe(() => ref.dismiss());
          ref
            .afterDismissed()
            .pipe(take(1))
            .subscribe(() => {
              this.submitted = false;
              if (!this.isReadOnly) this.editForm.enable({ emitEvent: false });
              this.editForm.markAsPristine();
              this.editForm.markAsUntouched();
              this.editForm.updateValueAndValidity({
                onlySelf: false,
                emitEvent: false,
              });
            });
        }
        this.reviewService.resetPatchState();
      });
  }

  private mapStatus(val: unknown): string {
    const statusMap: Record<string, string> = {
      preparation: '準備中',
      processing: 'レビュー中',
      finished: '完了',
    };
    return statusMap[val as string] ?? '';
  }

  private mapReviewResult(val: unknown): string {
    const reviewResultMap: Record<string, string> = {
      approved: '承認',
      rejected: '却下',
      null: '未承認',
    };
    return reviewResultMap[val as string] ?? '';
  }

  futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const inputDate = new Date(control.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (inputDate > today) {
        this.errorMessage =
          '承認・却下日は本日または過去の日付を入力してください';
        return { futureDate: true };
      } else {
        this.errorMessage = this.errorMessageDefault;
        return null;
      }
    };
  }

  private reviewResultEnumValidator(): ValidatorFn {
    return (c) =>
      c.value === null || ['approved', 'rejected'].includes(c.value)
        ? null
        : { invalidReviewResult: true };
  }

  private persistCurrentDraft(): void {
    if (!this.selectedItem) return;
    if (this.isReadOnly) return;

    const { comment, reviewed, reviewResult } = this.editForm.value as {
      reviewResult: 'approved' | 'rejected' | null;
      comment: string | null;
      reviewed: Date | string | null;
    };

    if (comment || reviewed || reviewResult) {
      this.reviewService.saveDraft(this.selectedItem.id, {
        comment: comment ?? null,
        reviewed: reviewed ?? null,
        reviewResult: reviewResult,
      });
    }
  }
  private buildColumns(): void {
    this.columns = [
      new TableColumn('id', '管理番号', true, undefined, 'center', (val) => {
        return typeof val === 'string' || val === null ? formatId(val) : '';
      }),
      new TableColumn('created', '作成日', true, undefined, 'center', (val) => {
        if (typeof val === 'string' && typeof val !== 'number') {
          const normalized = dateFormatter(val);
          return this.datePipe.transform(normalized, 'yyyy/MM/dd') ?? '';
        }
        return '';
      }),
      new TableColumn(
        'reviewResult',
        '承認状況',
        true,
        undefined,
        'center',
        (val) =>
          typeof val === 'string' || val === null
            ? this.mapReviewResult(val)
            : '',
      ),
      new TableColumn(
        'reviewed',
        '承認・却下日',
        true,
        undefined,
        'center',
        (val) =>
          typeof val === 'string' || val instanceof Date
            ? (this.datePipe.transform(val, 'yyyy/MM/dd') ?? '')
            : '',
      ),
      new TableColumn(
        'approverName',
        '承認・却下者',
        true,
        undefined,
        'center',
        (_: unknown, row: unknown) => (row as Review)?.approver?.name ?? '',
      ),
      new TableColumn(
        'comment',
        'コメント',
        true,
        (row) => {
          if (typeof row === 'object' && row !== null && 'comment' in row) {
            return (row as { comment?: string }).comment ?? '';
          }
          return '';
        },
        'left',
      ),
      new TableColumn(
        'status',
        'レビュー状況',
        true,
        undefined,
        'center',
        (val) =>
          typeof val === 'string' || val === null ? this.mapStatus(val) : '',
      ),
    ];
  }
}
